import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { ContractStateEnum, HotelCfarContract } from "redmond";

dayjs.extend(utc);

/**
 * Checks if a CFAR contract has expired using timezone-safe UTC comparison.
 *
 * This uses UTC for both times to avoid timezone bugs where international users
 * would see CFAR as expired when the backend knew it was still valid.
 */
export const hasCfarExpired = (cfar: HotelCfarContract | undefined): boolean =>
  cfar != null && dayjs.utc().isAfter(dayjs.utc(cfar.expired));

/**
 * Checks if a CFAR contract has been exercised
 */
export const isCfarExercised = (cfar: HotelCfarContract | undefined): boolean =>
  cfar != null && cfar.state?.ContractState === ContractStateEnum.Exercise;

/**
 * Checks if a CFAR contract is a negative premium CFAR
 * Uses defensive checks to avoid runtime errors with incomplete data
 */
export const isNegativePremiumCfar = (
  cfar: HotelCfarContract | undefined,
): boolean => {
  if (cfar == null) {
    return false;
  }

  // Defensive type checks to avoid accessing undefined properties
  const premiumValue = cfar?.premium?.amount?.fiat?.value;

  if (typeof premiumValue !== "number") {
    return false;
  }

  return premiumValue < 0;
};

/**
 * Determines whether to show the CFAR action button based on contract state
 *
 * The CFAR button should be shown when:
 * - A CFAR contract exists
 * - The contract has not been exercised
 * - The contract is either not expired OR it's a negative premium CFAR
 *
 * @param cfar - The hotel CFAR contract
 * @returns true if the CFAR action button should be displayed
 */
export const shouldShowCfarActionButton = (
  cfar: HotelCfarContract | undefined,
): boolean => {
  if (cfar == null) {
    return false;
  }

  if (isCfarExercised(cfar)) {
    return false;
  }

  // We want to show the CFAR action button for negative premium CFARs even if they are expired
  const isNegativePremium = isNegativePremiumCfar(cfar);
  if (hasCfarExpired(cfar) && !isNegativePremium) {
    return false;
  }

  return true;
};
